/**
 * Single source of truth for Kreatli plan facts used in marketing copy, pricing cards,
 * the plans modal, and comparison/pricing pages.
 *
 * Confirmed by the product owner (Oct 2026):
 * - Billing is monthly only. There is no annual plan or annual discount.
 * - Guest review links are included on every plan.
 * - Extra storage is an add-on ($3 per month per additional 100GB).
 * - No third-party storage integrations (Google Drive, Dropbox, etc.) exist. Google sign-in is supported.
 */

export type KreatliPlanId = 'creator' | 'team' | 'enterprise';

export interface KreatliPlanItem {
  label: string;
  tooltip?: string;
}

export interface KreatliStorageAddOn {
  pricePerMonth: number;
  blockGb: number;
  label: string;
}

export interface KreatliPlan {
  id: KreatliPlanId;
  name: string;
  /** Price per user per month in USD. `null` means custom pricing. */
  priceMonthly: number | null;
  /** Maximum members on the plan. `null` means custom. */
  memberLimit: number | null;
  /** Included storage, e.g. "500GB". */
  storageLabel: string;
  storageAddOn: KreatliStorageAddOn | null;
  description: string;
  previousPlan: string | null;
  limits: KreatliPlanItem[];
  features: KreatliPlanItem[];
}

export const BILLING_PERIOD = 'monthly' as const;

export const TRIAL_DAYS = 7;

export const STORAGE_ADD_ON: KreatliStorageAddOn = {
  pricePerMonth: 3,
  blockGb: 100,
  label: '$3 per month per additional 100GB',
};

/** Free Exploration mode: public, no credit card. */
export const EXPLORATION_MODE = {
  name: 'Exploration',
  priceMonthly: 0,
  projectLimit: 1,
  fileLimit: 2,
  requiresCard: false,
  label: '1 project, 2 files, no credit card',
} as const;

export const GUEST_REVIEW_LINKS_FEATURE: KreatliPlanItem = { label: 'Guest review links' };

export const KREATLI_PLANS: KreatliPlan[] = [
  {
    id: 'creator',
    name: 'Creator',
    priceMonthly: 14,
    memberLimit: 3,
    storageLabel: '500GB',
    storageAddOn: STORAGE_ADD_ON,
    description: 'Designed for solo creators and teams who want to organize projects, review work, and create faster.',
    previousPlan: null,
    limits: [
      { label: 'Up to 3 members' },
      { label: '500GB Storage', tooltip: STORAGE_ADD_ON.label },
      { label: 'Unlimited projects & reviewers' },
    ],
    features: [
      { label: 'Timecoded video review' },
      GUEST_REVIEW_LINKS_FEATURE,
      { label: 'Shared asset libraries' },
    ],
  },
  {
    id: 'team',
    name: 'Team',
    priceMonthly: 29,
    memberLimit: 10,
    storageLabel: '2TB',
    storageAddOn: STORAGE_ADD_ON,
    description:
      'Built for established teams running multiple projects with shared assets, structured reviews, and consistent workflows.',
    previousPlan: 'Creator',
    limits: [{ label: 'Up to 10 members' }, { label: '2TB Storage', tooltip: STORAGE_ADD_ON.label }],
    features: [
      { label: 'File organization and management' },
      { label: 'Versioning and comparison' },
      { label: 'Asset-linked conversations' },
      { label: 'Comment threads and annotations' },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceMonthly: null,
    memberLimit: null,
    storageLabel: 'Custom',
    storageAddOn: null,
    description:
      'Tailored for enterprise organizations that need advanced controls, custom workflows, and dedicated support.',
    previousPlan: 'Team',
    limits: [{ label: 'Custom members' }, { label: 'Custom storage' }],
    features: [
      { label: 'Single Sign-On (SSO)' },
      { label: 'Dedicated Account Manager' },
      { label: 'Custom Integrations & Features' },
    ],
  },
];

export function getKreatliPlan(id: KreatliPlanId): KreatliPlan {
  const plan = KREATLI_PLANS.find((item) => item.id === id);
  if (!plan) {
    throw new Error(`Unknown Kreatli plan: ${id}`);
  }
  return plan;
}

/**
 * Cheapest Kreatli plan that fits `seats` members, with the monthly total in USD.
 * Creator up to 3 seats, Team up to 10, otherwise Enterprise (custom pricing, `total: null`).
 */
export function getKreatliMonthlyCost(seats: number): { plan: KreatliPlan; total: number | null } {
  const normalizedSeats = Math.max(1, Math.ceil(seats));
  const plan =
    KREATLI_PLANS.filter((item) => item.priceMonthly !== null && item.memberLimit !== null)
      .sort((a, b) => (a.priceMonthly ?? 0) - (b.priceMonthly ?? 0))
      .find((item) => normalizedSeats <= (item.memberLimit ?? 0)) ?? getKreatliPlan('enterprise');

  return {
    plan,
    total: plan.priceMonthly === null ? null : plan.priceMonthly * normalizedSeats,
  };
}
